import { Color, Spacing } from '@persona-id/design-tokens';
import { useMemo } from 'react';
import styled from 'styled-components';

import HelpIcon from 'dashboard/components-lib/HelpIcon';
import { Period } from 'dashboard/lib/unixTime';
import {
  fillChartData,
  generateDomain,
  getEffectiveInterval,
  getPeriod,
} from 'dashboard/lib/utils/charting';
import {
  formatChartDate,
  formatChartDateHourChart,
  formatChartMonthName,
} from 'dashboard/lib/utils/formatters';
import { MetricsInquiryVolumeDatum } from 'dashboard/models/metrics';
import { MetricsFilterParams } from 'dashboard/redux/actions/metrics-inquiry';
import { MetricsState } from 'dashboard/redux/reducers/metrics-inquiry';
import HighlightCard, {
  formatInquiryVolumeMetrics,
} from 'dashboard/views/DashboardInquiryAnalyticsVolumeV2View/HighlightCard';
import InquiryComposedChart from 'dashboard/views/DashboardInquiryAnalyticsVolumeV2View/InquiryComposedChart';
import FunnelGranularitySelector from 'dashboard/views/InquiryFunnelAnalyticsDetailedView/FunnelGranularitySelector';
import Card from 'design-system/components/Card';

const COLORS = [
  Color.CoreTeal100,
  Color.CorePurple800,
  Color.CoreTeal900,
  Color.CoreGreen900,
  Color.CoreOrange800,
  Color.CoreYellow900,
  Color.CoreRed900,
  Color.CoreBlue800,
  Color.CoreSlate700,
];

const TOOLTIP_COPY_BY_METRIC = {
  created: 'Number of inquiries created to collect information from end-users.',
  started: '% of created inquiries that viewed the first screen of the inquiry flow.',
  interacted: '% of started inquiries that interacted with the first screen of the inquiry flow.',
  finished: '% of started inquiries that reached the end of the inquiry flow.',
  success: '% of finished inquiries that reached an inquiry status of completed or approved.',
  rejected: '% of finished inquiries that reached an inquiry status of failed or declined. ',
  medianCompletedTime:
    'The median amount of time the user has interacted with the inquiry flow from start to finish',
  pendingReviewRate: '% of finished inquiries that are currently marked for review.',
  startedRate: '% of created inquiries that viewed the first screen of the inquiry flow.',
  rejectedRate: '% of finished inquiries that reach an inquiry status of failed or declined.',
  approvedRate: '% of marked for review inquiries that were approved.',
  decisionedRate: '% of marked for review inquiries that were decisioned.',
  expirationStartedRate: '% of started inquiries that dropped off before finishing.',
  expirationCreatedRate: '% of created inquiries that dropped off before finishing.',
  markedForReviewRate: '% of finished inquiries that were ever marked for review.',
  markedForReview: 'Number of inquiries that were ever marked for review.',
  finishedRate: '% of started inquiries that reached the end of the inquiry flow.',
  autoDecisionedRate:
    '% of finished inquiries that were decisioned (approved or declined) automatically.',
  manuallyDecisionedRate:
    '% of finished inquiries that were decisioned (approved or declined) manually.',
};

const legendIcons = {
  pending_review_rate: <HelpIcon>{TOOLTIP_COPY_BY_METRIC.pendingReviewRate}</HelpIcon>,
  started_rate: <HelpIcon>{TOOLTIP_COPY_BY_METRIC.startedRate}</HelpIcon>,
  started: <HelpIcon>{TOOLTIP_COPY_BY_METRIC.started}</HelpIcon>,
  rejected_rate: <HelpIcon>{TOOLTIP_COPY_BY_METRIC.rejectedRate}</HelpIcon>,
  success_rate: <HelpIcon>{TOOLTIP_COPY_BY_METRIC.success}</HelpIcon>,
  approved_rate: <HelpIcon>{TOOLTIP_COPY_BY_METRIC.approvedRate}</HelpIcon>,
  decisioned_rate: <HelpIcon>{TOOLTIP_COPY_BY_METRIC.decisionedRate}</HelpIcon>,
  expiration_started_rate: <HelpIcon>{TOOLTIP_COPY_BY_METRIC.expirationStartedRate}</HelpIcon>,
  expiration_created_rate: <HelpIcon>{TOOLTIP_COPY_BY_METRIC.expirationCreatedRate}</HelpIcon>,
  marked_for_review_rate: <HelpIcon>{TOOLTIP_COPY_BY_METRIC.markedForReviewRate}</HelpIcon>,
  marked_for_review: <HelpIcon>{TOOLTIP_COPY_BY_METRIC.markedForReview}</HelpIcon>,
  finished_rate: <HelpIcon>{TOOLTIP_COPY_BY_METRIC.finishedRate}</HelpIcon>,
  finished: <HelpIcon>{TOOLTIP_COPY_BY_METRIC.finished}</HelpIcon>,
  created: <HelpIcon>{TOOLTIP_COPY_BY_METRIC.created}</HelpIcon>,
  auto_decisioned_rate: <HelpIcon>{TOOLTIP_COPY_BY_METRIC.autoDecisionedRate}</HelpIcon>,
  manual_decisioned_rate: <HelpIcon>{TOOLTIP_COPY_BY_METRIC.manuallyDecisionedRate}</HelpIcon>,
};

const Container = styled.section``;

export interface InquiryMetricsProps
  extends Pick<
    MetricsState,
    'volume' | 'volumeOverTime' | 'medianCompletion' | 'volumeDelta' | 'medianCompletionDelta'
  > {
  showInteractedRate: boolean;
  handleUpdateGranularity: (period: Period) => void;
  overviewPeriod: Period;
  lastQueryFilters: MetricsFilterParams;
}

export enum FilterDimensions {
  CountryCode = 'country_code',
  PlatformIntegration = 'platform',
  DeviceGroup = 'device_group',
}

const calculatePercentage = (numerator: number, denominator: number): number | null => {
  if (denominator === 0) {
    return null;
  }
  return (numerator * 100) / denominator;
};

const augmentWithRates = (
  data: MetricsInquiryVolumeDatum[],
): (MetricsInquiryVolumeDatum & MetricsInquiryVolumeRates)[] => {
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  return data.map((datum) => {
    return {
      ...datum,
      // created chart
      started_rate: calculatePercentage(datum.started, datum.created),
      expiration_created_rate: calculatePercentage(datum.expired_not_started, datum.created),
      finished_rate: calculatePercentage(datum.finished_started, datum.started),
      expiration_started_rate: calculatePercentage(datum.expired_started, datum.started),
      success_rate: calculatePercentage(datum.succeeded_finished, datum.finished),
      rejected_rate: calculatePercentage(datum.rejected_finished, datum.finished),
      pending_review_rate: calculatePercentage(datum.pending_finished, datum.finished),
      marked_for_review_rate: calculatePercentage(
        datum.ever_marked_for_review_finished,
        datum.finished,
      ),
      auto_decisioned_rate: calculatePercentage(datum.auto_finished, datum.finished),
      manual_decisioned_rate: calculatePercentage(datum.manual_finished, datum.finished),
      decisioned_rate: calculatePercentage(
        datum.decisioned_marked_for_review,
        datum.marked_for_review,
      ),
      approved_rate: calculatePercentage(
        datum.approved_decisioned_marked_for_review,
        datum.decisioned_marked_for_review,
      ),
    };
  });
};

export interface MetricsInquiryVolumeRates {
  started_rate: number;
  expiration_created_rate: number;
  finished_rate: number;
  expiration_started_rate: number;
  success_rate: number;
  rejected_rate: number;
  pending_review_rate: number;
  marked_for_review_rate: number;
  auto_decisioned_rate: number;
  manual_decisioned_rate: number;
  decisioned_rate: number;
  approved_rate: number;
}

const HighlightContainer = styled.div<{ showInteractedRate: boolean }>`
  width: 100%;
  display: grid;
  grid-template-columns: repeat(${({ showInteractedRate }) => (showInteractedRate ? 7 : 6)}, auto);
  grid-gap: ${Spacing[4]};
`;

const HeaderContainer = styled.div`
  display: flex;
  flex-direction: row;
`;

const InquiryMetrics = ({
  volume,
  volumeDelta,
  volumeOverTime,
  medianCompletion,
  medianCompletionDelta,
  showInteractedRate,
  overviewPeriod,
  handleUpdateGranularity,
  lastQueryFilters,
}: InquiryMetricsProps) => {
  const effectivePeriod = overviewPeriod;
  const recommendedPeriod = getPeriod(
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    lastQueryFilters.createdAtStart,
    lastQueryFilters.createdAtEnd,
  );

  const filledData = useMemo(() => {
    const data = augmentWithRates(volumeOverTime.data);

    return fillChartData(
      data,
      {
        created: 0,
        started: 0,
        finished: 0,
        approved_rate: 0,
        expired_not_started: 0,
        auto_decisioned_rate: 0,
        decisioned_rate: 0,
        expiration_created_rate: 0,
        expiration_started_rate: 0,
        started_with_interaction: 0,
        finished_rate: 0,
        expired_started: 0,
        manual_decisioned_rate: 0,
        marked_for_review: 0,
        marked_for_review_rate: 0,
        started_rate: 0,
        approved_decisioned_marked_for_review: 0,
        auto_finished: 0,
        decisioned_marked_for_review: 0,
        ever_marked_for_review_finished: 0,
        finished_started: 0,
        manual_finished: 0,
        pending_finished: 0,
        pending_review_rate: 0,
        rejected_finished: 0,
        rejected_rate: 0,
        succeeded_finished: 0,
        success_rate: 0,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        updated_at: null,
      },
      lastQueryFilters?.createdAtStart && lastQueryFilters?.createdAtEnd
        ? generateDomain(
            [
              // These are in MS, need to divide
              Date.parse(lastQueryFilters.createdAtStart) / 1000,
              Date.parse(lastQueryFilters.createdAtEnd) / 1000,
            ],
            overviewPeriod,
          )
        : volumeOverTime.domain,
      effectivePeriod,
      false,
      true,
    );
  }, [volumeOverTime.data, volumeOverTime.domain, volumeOverTime.period, overviewPeriod]);

  const decisionedRates: Partial<Record<keyof MetricsInquiryVolumeRates, string>> =
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    volume.template_uses_decisioning
      ? {
          pending_review_rate: 'Needs review rate',
          marked_for_review_rate: 'Ever marked-for-review rate',
          auto_decisioned_rate: 'Auto-decisioned rate',
          manual_decisioned_rate: 'Manual-decisioned rate',
        }
      : {};

  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const formattedMetrics = formatInquiryVolumeMetrics(volume, medianCompletion);

  const isInquiryVolumeDeltaEnabled = volumeDelta != null && medianCompletionDelta != null;

  const formattedDeltaMetrics = isInquiryVolumeDeltaEnabled
    ? formatInquiryVolumeMetrics(volumeDelta, medianCompletionDelta)
    : null;
  const domain = isInquiryVolumeDeltaEnabled ? volumeDelta.domain : null;

  const interval = getEffectiveInterval(recommendedPeriod, effectivePeriod);

  const xFormatter =
    effectivePeriod === Period.Hour && recommendedPeriod === effectivePeriod
      ? formatChartDateHourChart
      : effectivePeriod === Period.Month
        ? formatChartMonthName
        : formatChartDate;

  return (
    <Container data-test='inquiry-metrics__inquiry-status-section'>
      <Card>
        <Card.Body>
          <Card.Title>Highlights</Card.Title>
          <HighlightContainer showInteractedRate={showInteractedRate}>
            <HighlightCard
              label='Inquiries created'
              toolTipCopy={TOOLTIP_COPY_BY_METRIC.created}
              isInquiryVolumeDeltaEnabled={isInquiryVolumeDeltaEnabled}
              metricValue={formattedMetrics.created}
              previousPeriodMetricValue={formattedDeltaMetrics?.created}
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              previousPeriodDomain={domain}
              invertColors={false}
              deltaType='relative-percentage'
            />
            <HighlightCard
              label='Started rate'
              toolTipCopy={TOOLTIP_COPY_BY_METRIC.started}
              isInquiryVolumeDeltaEnabled={isInquiryVolumeDeltaEnabled}
              metricValue={formattedMetrics.started}
              previousPeriodMetricValue={formattedDeltaMetrics?.started}
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              previousPeriodDomain={domain}
              invertColors={false}
            />
            {showInteractedRate && (
              <HighlightCard
                label='Interacted rate'
                toolTipCopy={TOOLTIP_COPY_BY_METRIC.interacted}
                isInquiryVolumeDeltaEnabled={isInquiryVolumeDeltaEnabled}
                metricValue={formattedMetrics.interacted}
                previousPeriodMetricValue={formattedDeltaMetrics?.interacted}
                /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                previousPeriodDomain={domain}
                invertColors={false}
              />
            )}
            <HighlightCard
              label='Finished rate'
              toolTipCopy={TOOLTIP_COPY_BY_METRIC.finished}
              isInquiryVolumeDeltaEnabled={isInquiryVolumeDeltaEnabled}
              metricValue={formattedMetrics.finished}
              previousPeriodMetricValue={formattedDeltaMetrics?.finished}
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              previousPeriodDomain={domain}
              invertColors={false}
            />
            <HighlightCard
              label='Success rate'
              toolTipCopy={TOOLTIP_COPY_BY_METRIC.success}
              isInquiryVolumeDeltaEnabled={isInquiryVolumeDeltaEnabled}
              metricValue={formattedMetrics.success}
              previousPeriodMetricValue={formattedDeltaMetrics?.success}
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              previousPeriodDomain={domain}
              invertColors={false}
            />
            <HighlightCard
              label='Rejected rate'
              toolTipCopy={TOOLTIP_COPY_BY_METRIC.rejected}
              isInquiryVolumeDeltaEnabled={isInquiryVolumeDeltaEnabled}
              metricValue={formattedMetrics.rejected}
              previousPeriodMetricValue={formattedDeltaMetrics?.rejected}
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              previousPeriodDomain={domain}
              invertColors={true}
            />
            <HighlightCard
              label='Time to finish (median)'
              toolTipCopy={TOOLTIP_COPY_BY_METRIC.medianCompletedTime}
              isInquiryVolumeDeltaEnabled={isInquiryVolumeDeltaEnabled}
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              metricValue={formattedMetrics.medianCompletedTime}
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              previousPeriodMetricValue={formattedDeltaMetrics?.medianCompletedTime}
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              previousPeriodDomain={domain}
              invertColors={true}
              deltaType='duration'
            />
          </HighlightContainer>
        </Card.Body>
      </Card>
      <Card>
        <Card.Body>
          <HeaderContainer>
            <Card.Title>Created</Card.Title>
            <FunnelGranularitySelector
              value={overviewPeriod}
              handleUpdateGranularity={handleUpdateGranularity}
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              dateRange={[lastQueryFilters.createdAtStart, lastQueryFilters.createdAtEnd]}
            />
          </HeaderContainer>
          <InquiryComposedChart
            // onDeepLink={onDeepLink}
            data={filledData}
            period={effectivePeriod}
            xFormatter={xFormatter}
            interval={interval}
            barKeys={{ created: 'Created volume' }}
            lineKeys={{ started_rate: 'Started rate', expiration_created_rate: 'Expiration rate' }}
            initialFilteredLegendKeys={['created', 'started_rate']}
            colors={COLORS.slice(0, 3)}
            legendIcons={legendIcons}
          />
        </Card.Body>
      </Card>
      <Card>
        <Card.Body>
          <Card.Title>Started</Card.Title>
          <InquiryComposedChart
            // onDeepLink={onDeepLink}
            data={filledData}
            period={effectivePeriod}
            xFormatter={xFormatter}
            interval={interval}
            barKeys={{ started: 'Started volume' }}
            lineKeys={{
              finished_rate: 'Finished rate',
              expiration_started_rate: 'Expiration rate (started)',
            }}
            initialFilteredLegendKeys={['started', 'finished_rate']}
            colors={COLORS.slice(0, 3)}
            legendIcons={legendIcons}
          />
        </Card.Body>
      </Card>
      <Card>
        <Card.Body>
          <Card.Title>Finished</Card.Title>
          <InquiryComposedChart
            // onDeepLink={onDeepLink}
            data={filledData}
            period={effectivePeriod}
            xFormatter={xFormatter}
            interval={interval}
            barKeys={{ finished: 'Finished volume' }}
            linesLabel='Status breakdown'
            lineKeys={{
              success_rate: 'Success rate',
              rejected_rate: 'Rejected rate',
              ...decisionedRates,
            }}
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            lineGroups={
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              volume.template_uses_decisioning
                ? {
                    'Status breakdown': {
                      success_rate: 'Success rate',
                      rejected_rate: 'Rejected rate',
                      pending_review_rate: 'Needs review rate',
                    },

                    'Decisioning metrics': {
                      marked_for_review_rate: 'Ever marked-for-review rate',
                      auto_decisioned_rate: 'Auto-decisioned rate',
                      manual_decisioned_rate: 'Manual-decisioned rate',
                    },
                  }
                : null
            }
            initialFilteredLegendKeys={['finished', 'success_rate']}
            colors={COLORS.slice(0, 3).concat(
              Object.keys(decisionedRates).map((_, index) => COLORS[index + 3]),
            )}
            legendIcons={legendIcons}
          />
        </Card.Body>
      </Card>

      <Card>
        <Card.Body>
          <Card.Title>Marked for Review</Card.Title>
          <InquiryComposedChart
            // onDeepLink={onDeepLink}
            data={filledData}
            period={effectivePeriod}
            xFormatter={xFormatter}
            interval={interval}
            barKeys={{ marked_for_review: 'Marked for review volume' }}
            lineKeys={{
              decisioned_rate: 'Decisioned rate',
              approved_rate: 'Approved rate',
            }}
            initialFilteredLegendKeys={['marked_for_review', 'decisioned_rate']}
            colors={COLORS.slice(0, 3)}
            legendIcons={legendIcons}
          />
        </Card.Body>
      </Card>
    </Container>
  );
};

export default InquiryMetrics;
